import { jwtDecode } from 'jwt-decode';
import { SessionState } from '../../store/slices/sessionSlice';

const TOKEN_KEY = 'token';

// ── character-substitution tables (ported from Angular's CommonService) ──────

const SYMBOL_TO_LETTER: Record<string, string> = {
  '!': 'a',
  '@': 'B',
  '#': 'c',
  $: 'D',
  '%': 'e',
  '^': 'F',
  '&': 'g',
  '?': 'i',
  '+': 'J',
  '=': 'k',
  '|': 'L',
  '~': 'm',
  '/': 'N',
  '-': 'o',
  _: 'P',
  '{': 'q',
  '}': 'R',
  '[': 's',
  ']': 'T',
  '(': 'U',
  ')': 'v',
  '<': 'W',
  '>': 'x',
  ':': 'Y',
  ';': 'z',
  '*': '.',
};

const ALPHA_TO_NUMBER: Record<string, string> = {
  A: '0',
  b: '1',
  C: '2',
  d: '3',
  E: '4',
  f: '5',
  G: '6',
  h: '7',
  I: '8',
  j: '9',
};

// Equivalent of Angular's CommonService.assignLetters()
export function assignLetters(text: string): string {
  let result = '';
  for (let i = 0; i < text.length; i++) {
    result += SYMBOL_TO_LETTER[text[i]] ?? text[i];
  }
  return result;
}

// Equivalent of Angular's CommonService.alphabetToNumber()
export function alphabetToNumber(text: string): string {
  let result = '';
  for (let i = 0; i < text.length; i++) {
    result += ALPHA_TO_NUMBER[text[i]] ?? text[i];
  }
  return result;
}

// Equivalent of Angular's CommonService.decryptionData()
// Reverses the backend's symbol-substitution to produce a valid JWT string
export function decryptionData(token: string): string {
  return assignLetters(token);
}

// ── main decode function ─────────────────────────────────────────────────────

export function decodeSessionFromStorage(): Omit<
  SessionState,
  'loaded'
> | null {
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    if (!raw) return null;

    // Step 1: reverse symbol-substitution → valid JWT
    const jwt = decryptionData(raw);

    // Step 2: decode JWT payload (no signature verification)
    const d = jwtDecode<Record<string, unknown>>(jwt);

    const usertypeid = String(d['emis_usertype'] ?? '');
    const isOfficeUser = [5, 6, 9, 10, 22].includes(Number(usertypeid));
    if (isOfficeUser && d['off_key_id']) {
      localStorage.setItem('officeKeyID', String(d['off_key_id']));
    }

    const STATE_TYPES = ['5', '21', '52'];
    const DISTRICT_TYPES = ['9', '10', '37', '38', '36', '19', '13'];
    const BLOCK_TYPES = ['39', '6'];

    let user_role_level = '';
    if (STATE_TYPES.includes(usertypeid)) {
      user_role_level = 'state';
    } else if (DISTRICT_TYPES.includes(usertypeid)) {
      user_role_level = 'district';
    } else if (BLOCK_TYPES.includes(usertypeid)) {
      user_role_level = 'block';
    }
    return {
      // identity
      userId: Number(d['emis_user_id'] ?? d['user_id']) || 0,
      username: String(d['emis_username'] ?? ''),
      emis_username: String(d['emis_username'] ?? ''),
      name: String(d['name'] ?? ''),
      teacher_name: String(d['teacher_name'] ?? ''),
      Socialname: String(d['Socialname'] ?? ''),
      designation: String(d['designation'] ?? ''),
      phnumber: String(d['mbl_nmbr'] ?? ''),
      email: String(d['email'] ?? ''),

      // school
      schoolName: String(d['school_name'] ?? ''),
      schoolId: Number(d['school_id']) || 0,
      schoolKeyId: String(d['school_key_id'] ?? ''),
      schoolTypeId: Number(d['school_type_id']) || 0,
      schlId: String(d['schlId'] ?? ''),
      highClass: Number(d['high_class']) || 0,
      lowClass: Number(d['low_class']) || 0,

      // teacher
      teacherId: Number(d['teacher_id']) || 0,
      teacherType: String(d['teacher_type'] ?? ''),
      type_teacher: String(d['type_teacher'] ?? ''),
      subjects: String(d['subjects'] ?? ''),
      subject_id: String(d['subject_id'] ?? ''),

      // user type / role
      usertype: String(d['user_type'] ?? ''),
      usertypeid,
      emisUsertype1: String(d['emis_usertype1'] ?? ''),
      roleId: Number(d['roleId']) || 0,
      offKeyId: isOfficeUser ? String(d['off_key_id'] ?? '') : '',

      // category
      cate_id: String(d['cate_id'] ?? ''),
      catty_id: String(d['catty_id'] ?? ''),
      cateType: String(d['cate_type'] ?? ''),
      category_type: String(d['category_type'] ?? ''),
      manage_id: String(d['manage_id'] ?? ''),

      // location
      districtId: String(d['district_id'] ?? ''),
      districtName: String(d['district_name'] ?? ''),
      blockId: String(d['block_id'] ?? ''),
      blockName: String(d['block_name'] ?? ''),
      edu_dist_name: String(d['edu_dist_name'] ?? ''),
      edn_dist_name: String(d['edn_dist_name'] ?? ''),
      // Angular: edn_dist_id takes priority over edu_district_id
      edu_district_id: String(d['edn_dist_id'] ?? d['edu_district_id'] ?? ''),

      // module permissions
      mod2: String(d['mod2'] ?? ''),

      // switch login
      switch_login: String(d['swith_login'] ?? ''), // note: typo in JWT key
      switch_designation: String(d['switch_designation'] ?? ''),

      // token / meta — authToken is the decoded JWT (same as Angular)
      authToken: jwt,
      iat: Number(d['iat']) || 0,
      tokenExpiry: Number(d['exp']) || 0,

      user_role_level: user_role_level,
    };
  } catch (err) {
    console.error('[Session] Failed to decode token:', err);
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Sliding session (client-side token expiry extension — no network).
//
// The stored "token" is a valid JWT run through a reversible symbol→letter
// substitution. decode() reverses it to a real JWT; encode() applies it back.
// The backend only reads JWT claims (it does not verify the signature), so
// bumping `exp` locally and re-encoding is a valid way to keep an active
// session alive. If the backend ever starts verifying the signature, this
// approach must be replaced with a server /refresh endpoint.
// ─────────────────────────────────────────────────────────────────────────────

// Inverse of SYMBOL_TO_LETTER (letter → symbol). Values are unique, so this is
// a clean bijection — encode() is the exact inverse of decode().
const LETTER_TO_SYMBOL: Record<string, string> = Object.fromEntries(
  Object.entries(SYMBOL_TO_LETTER).map(([symbol, letter]) => [letter, symbol])
);

/** decode(raw) -> valid JWT. Reverses the backend's symbol substitution. */
export function decode(raw: string): string {
  return assignLetters(raw);
}

/** encode(validJWT) -> raw stored form. Exact inverse of decode(). */
export function encode(jwt: string): string {
  let result = '';
  for (let i = 0; i < jwt.length; i++) {
    result += LETTER_TO_SYMBOL[jwt[i]] ?? jwt[i];
  }
  return result;
}

// base64url → JSON string.
function base64UrlDecode(b64url: string): string {
  let b64 = b64url.replace(/-/g, '+').replace(/_/g, '/');
  // Pad to a multiple of 4 so atob doesn't throw.
  while (b64.length % 4 !== 0) b64 += '=';
  return atob(b64);
}

// JSON string → base64url (no padding).
function base64UrlEncode(json: string): string {
  return btoa(json).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * True when there's no token, no readable `exp`, or the token has expired.
 * Any failure is treated as "expired" (fail-safe).
 */
export function isSessionExpired(): boolean {
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    if (!raw) return true;
    const jwt = decode(raw);
    const payload = jwtDecode<{ exp?: number }>(jwt);
    if (typeof payload.exp !== 'number') return true;
    return Date.now() >= payload.exp * 1000;
  } catch {
    return true;
  }
}

/**
 * Silently extend the stored token's `exp` by one hour (no network). Keeps the
 * header & signature untouched; only the payload's `exp` claim changes. On any
 * error the token is left exactly as-is.
 */
export function refreshTokenExpiry(): void {
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    if (!raw) return;

    const jwt = decode(raw);
    const parts = jwt.split('.');
    if (parts.length !== 3) return;
    const [header, payloadB64, signature] = parts;

    const payload = JSON.parse(base64UrlDecode(payloadB64)) as {
      exp?: number;
      [k: string]: unknown;
    };
    if (typeof payload.exp !== 'number') return;

    payload.exp += 3600; // +1 hour

    const newPayloadB64 = base64UrlEncode(JSON.stringify(payload));
    const newJwt = `${header}.${newPayloadB64}.${signature}`;
    localStorage.setItem(TOKEN_KEY, encode(newJwt));
  } catch (err) {
    // Never surface or expose the token; just log the failure.
    console.error('[session] failed to extend token expiry:', err);
  }
}
